<template>
    <AppModal ref="modal" :title="$t('imageGenerationModal.modalTitle')" size="full" @on-hide="hide">
        <WizardForm
            :steps="steps"
            @submit="hide"
        />
    </AppModal>
</template>
<script>
import AppModal from '../../../../../../../../components/new/modal/AppModal.vue';
import WizardForm from '../../../../../../../../components/new/form/wizardForm/WizardForm.vue';

import { defineAsyncComponent } from 'vue';
import { httpClient } from '../../../../../../../../services/HttpClient';
import { generateUrl } from '../../../../../../../../utils';

const TemplatesStep = defineAsyncComponent(() => import('./wizardSteps/TemplatesStep.vue'));
const SettingsStep = defineAsyncComponent(() => import('./wizardSteps/SettingsStep.vue'));
const GenerationStep = defineAsyncComponent(() => import('./wizardSteps/GenerationStep.vue'));

export default {
    name: 'ImageGenerationModal',
    components: {
        AppModal, WizardForm
    },
    emits: ['saveGeneratedImage'],
    data() {
        return {
            product: {
                name: null,
                description: null,
                images: null
            },
            productAttributes: null,
            imageForGeneration: null,
            selectedTemplateKey: null,
            selectedFormatKey: '1:1',
            selectedAttributes: {},
            selectedEditablePhotoProperties: [],
            selectedReference: null,
            integrationLanguages: [],
            selectedResolutionKey: '1K',
            selectedLanguageKey: null,
            tokenBalance: null,
        }
    },
    methods: {
        async show(imageUrl, productAttributes, product, integrationLanguages) {
            this.productAttributes = productAttributes;
            this.imageForGeneration = await this.imageToBase64File(imageUrl);
            this.integrationLanguages = integrationLanguages;
            this.selectedLanguageKey = integrationLanguages[0] || null;
            this.product = {
                name: product.names[integrationLanguages[0]] || Object.values(product.names)[0],
                description: product.descriptions[integrationLanguages[0]] || Object.values(product.descriptions)[0],
                images: product.images
            };
            await this.fetchBalance();
            this.$refs.modal.show();
        },
        hide() {
            this.$refs.modal.hide();
            this.productAttributes = null;
            this.product = {
                name: null,
                description: null,
                images: null
            };
            this.imageForGeneration = null;
            this.selectedTemplateKey = null;
            this.selectedFormatKey = '1:1';
            this.selectedAttributes = {};
            this.selectedEditablePhotoProperties = [];
            this.selectedReference = null;
            this.integrationLanguages = [];
            this.selectedResolutionKey = '1K';
            this.selectedLanguageKey = null;
            this.tokenBalance = null;
        },
        async fetchBalance() {
            await httpClient.get(`/projects/${this.$route.params.projectUuid}/core/ai-transactions/balance`).then((response) => {
                this.tokenBalance = response.data.balance;
            })
        },
        base64ToFile(base64String, filename) {
            const base64Data = base64String.replace(/^data:image\/[a-z+]+;base64,/, '');
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);

            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }

            const byteArray = new Uint8Array(byteNumbers);
            return new File([byteArray], filename, { type: 'image/png' });
        },
        getImageDataUrlFromBase64(base64String) {
            if (base64String.startsWith('data:image')) {
                return base64String;
            }
            return `data:image/png;base64,${base64String}`;
        },
        async urlToBase64(imageUrl) {
            const response = await httpClient.post(generateUrl('proxy/http-request'), {
                method: 'GET',
                url: imageUrl
            }, true, {
                responseType: 'arraybuffer',
            });

            if (response.status !== 200) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const bytes = new Uint8Array(response.data);
            const binaryString = bytes.reduce((data, byte) => data + String.fromCharCode(byte), '');
            return btoa(binaryString);
        },
        async imageToBase64File(imageUrl, fileName = 'image.jpg') {
            const base64 = await this.urlToBase64(imageUrl);
            return { file: this.base64ToFile(base64, fileName), base64 };
        },
        async refreshBalance() {
            const response = await httpClient.get(`/projects/${this.$route.params.projectUuid}/core/ai-transactions/balance`);
            this.tokenBalance = response.data.balance;
        },
    },
    computed: {
        steps() {
            return [
                {
                    name: this.$t('imageGenerationModal.steps.template'),
                    component: TemplatesStep,
                    props: {
                        selectedTemplateKey: this.selectedTemplateKey,
                        'onUpdate:selectedTemplateKey': (templateKey) => {
                            this.selectedTemplateKey = templateKey;
                        }
                    }
                },
                {
                    name: this.$t('imageGenerationModal.steps.settings'),
                    component: SettingsStep,
                    props: {
                        selectedTemplateKey: this.selectedTemplateKey,
                        selectedFormatKey: this.selectedFormatKey,
                        'onUpdate:selectedFormatKey': (formatKey) => {
                            this.selectedFormatKey = formatKey;
                        },
                        productAttributes: this.productAttributes,
                        selectedAttributes: this.selectedAttributes,
                        'onUpdate:selectedAttributes': (attributes) => {
                            this.selectedAttributes = attributes;
                        },
                        selectedEditablePhotoProperties: this.selectedEditablePhotoProperties,
                        'onUpdate:selectedEditablePhotoProperties': (editablePhotoProperties) => {
                            this.selectedEditablePhotoProperties = editablePhotoProperties;
                        },
                        productImages: this.product.images,
                        selectedReference: this.selectedReference,
                        'onUpdate:selectedReference': (reference) => {
                            this.selectedReference = reference;
                        },
                        integrationLanguages: this.integrationLanguages,
                        selectedResolutionKey: this.selectedResolutionKey,
                        'onUpdate:selectedResolutionKey': (resolution) => {
                            this.selectedResolutionKey = resolution;
                        },
                        selectedLanguageKey: this.selectedLanguageKey,
                        'onUpdate:selectedLanguageKey': (language) => {
                            this.selectedLanguageKey = language;
                        },
                        base64ToFile: this.base64ToFile,
                        getImageDataUrlFromBase64: this.getImageDataUrlFromBase64,
                        urlToBase64: this.urlToBase64,
                    }
                },
                {
                    name: this.$t('imageGenerationModal.steps.generation'),
                    component: GenerationStep,
                    props: {
                        selectedTemplateKey: this.selectedTemplateKey,
                        imageForGeneration: this.imageForGeneration,
                        selectedFormatKey: this.selectedFormatKey,
                        'onSaveGeneratedImage': (files) => {
                            this.$emit('saveGeneratedImage', files);
                        },
                        onTokensFlushed: this.refreshBalance,
                        tokenBalance: this.tokenBalance,
                        selectedAttributes: this.selectedAttributes,
                        productAttributes: this.productAttributes,
                        product: this.product,
                        selectedEditablePhotoProperties: this.selectedEditablePhotoProperties,
                        selectedReference: this.selectedReference,
                        selectedLanguageKey: this.selectedLanguageKey,
                        selectedResolutionKey: this.selectedResolutionKey,
                        base64ToFile: this.base64ToFile,
                        getImageDataUrlFromBase64: this.getImageDataUrlFromBase64,
                        urlToBase64: this.urlToBase64,
                    }
                }
            ]
        }
    }
}
</script>